import React, { useState, useEffect } from 'react';
import useSound from '../hooks/useSound';
import styles from './NavBar.module.css';

interface NavBarProps {
  activeSection: string;
  onSectionChange: (section: 'hero' | 'about' | 'skills' | 'projects' | 'achievements' | 'responsibility' | 'contact') => void;
}

const NavBar: React.FC<NavBarProps> = ({ activeSection, onSectionChange }) => {
  const { playHover, playClick } = useSound();
  const [isMobile, setIsMobile] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 576);
    };
    
    // Initial check
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const toggleMenu = () => {
    playClick();
    setMenuOpen(!menuOpen);
  };
  
  const handleNavClick = (section: 'hero' | 'about' | 'skills' | 'projects' | 'achievements' | 'responsibility' | 'contact') => {
    playClick();
    onSectionChange(section);
    if (isMobile) {
      setMenuOpen(false);
    }
  };
  
  // Navigation items
  const navItems = [
    { id: 'hero', label: 'HOME', mobileLabel: 'HOME' },
    { id: 'about', label: 'ABOUT', mobileLabel: 'ABOUT' },
    { id: 'skills', label: 'SKILLS', mobileLabel: 'SKILLS' },
    { id: 'projects', label: 'PROJECTS', mobileLabel: 'PROJECTS' },
    { id: 'achievements', label: 'ACHIEVEMENTS', mobileLabel: 'ACHIEVEMENTS' },
    { id: 'responsibility', label: 'RESPONSIBILITY', mobileLabel: 'RESPONSIBILITY' },
    { id: 'contact', label: 'CONTACT', mobileLabel: 'CONTACT' }
  ] as const;
  
  return (
    <nav className={styles.nav}>
      <div className={styles.navContainer}>
        {/* Hamburger button for mobile view */}
        {isMobile && (
          <button 
            className={`${styles.hamburgerBtn} ${menuOpen ? styles.open : ''}`} 
            onClick={toggleMenu}
            onMouseEnter={playHover}
            aria-label="Toggle menu"
            style={{
              background: 'rgba(0, 0, 0, 0.9)',
              border: '2px solid #00ff00',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px'
            }}
          >
            {/* SVG Hamburger Icon */}
            <svg 
              width="30" 
              height="30" 
              viewBox="0 0 30 30" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              style={{ 
                filter: 'drop-shadow(0 0 5px #00ff00) drop-shadow(0 0 2px #ffffff)' 
              }}
            >
              <rect y="6" width="30" height="4" rx="2" fill="#00ff77" />
              <rect y="13" width="30" height="4" rx="2" fill="#00ffaa" />
              <rect y="20" width="30" height="4" rx="2" fill="#00ffdd" />
            </svg>
          </button>
        )}
        
        {/* Mobile Navigation Menu */}
        {isMobile && (
          <ul className={`${styles.mobileNavList} ${menuOpen ? styles.menuOpen : ''}`}>
            {navItems.map((item) => (
              <li
                key={item.id}
                className={styles.navItem}
                onMouseEnter={playHover}
                onClick={() => handleNavClick(item.id as any)}
                style={{ 
                  padding: '12px 0',
                  borderBottom: '1px solid rgba(0, 255, 0, 0.3)'
                }}
              >
                <span 
                  className={`${styles.navLink} ${activeSection === item.id ? styles.active : ''}`}
                  style={{ fontSize: '20px' }}
                >
                  {item.mobileLabel}
                </span>
              </li>
            ))}
          </ul>
        )}
        
        {/* Desktop Navigation Menu */}
        {!isMobile && (
          <ul className={styles.navList}>
            {navItems.map((item) => (
              <li
                key={item.id}
                className={styles.navItem}
                onMouseEnter={playHover}
                onClick={() => handleNavClick(item.id as any)}
              >
                <span className={`${styles.navLink} ${activeSection === item.id ? styles.active : ''}`}>
                  {item.label}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      
      {/* Add overlay when mobile menu is open */}
      {isMobile && menuOpen && (
        <div 
          onClick={() => setMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999
          }}
        />
      )}
    </nav>
  );
};

export default NavBar;